import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, EMPTY, Observable, of, Subscription, timer} from "rxjs";
import {catchError, delay, finalize, last, map, switchMap, take, tap} from "rxjs/operators";
import {GatewayType, IDeviceData} from "../interfaces/juconnGateway.response";
import {MqttBlockingService} from "./mqtt-blocking.service";
import {DataService} from "./data.service";
import {AlertService} from "./alert.service";
import {MqttGetDeviceDto} from '../interfaces/mqtt/mqtt-get-device.dto';
import {IInstallationDataResponse} from '../interfaces/installation-data.response';
import {Utils} from "../utils/utils";
import {MqttGetModuleConfigDto} from "../interfaces/mqtt/mqtt-get-module-config.dto";

@Injectable({
  providedIn: "root",
})
export class DeviceDataService implements OnDestroy {
  public gatewayId: string

  private readonly deviceSubject: BehaviorSubject<MqttGetDeviceDto> = new BehaviorSubject<MqttGetDeviceDto>(null);
  public readonly device$: Observable<MqttGetDeviceDto> = this.deviceSubject.asObservable();

  private readonly deviceDataSubject: BehaviorSubject<{
    deviceData: IDeviceData,
    isLoading: boolean,
    hasError: boolean
  }> = new BehaviorSubject<{ deviceData: IDeviceData, isLoading: boolean, hasError: boolean }>(undefined);
  public readonly deviceData$: Observable<{
    deviceData: IDeviceData,
    isLoading: boolean,
    hasError: boolean
  }> = this.deviceDataSubject.asObservable();

  private readonly timerForIRUpdateSubject: BehaviorSubject<{
    timer: number,
    totalSeconds: number
  }> = new BehaviorSubject(null);
  public readonly timerForIRUpdate$: Observable<{
    timer: number,
    totalSeconds: number
  }> = this.timerForIRUpdateSubject.asObservable();

  private readonly installationDataSubject: BehaviorSubject<IInstallationDataResponse> = new BehaviorSubject<IInstallationDataResponse>(null);
  public readonly installationData$: Observable<IInstallationDataResponse> = this.installationDataSubject.asObservable();

  private readonly implementedMetersSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  public readonly implementedMeters$: Observable<string[]> = this.implementedMetersSubject.asObservable();

  private readonly moduleConfigSubject: BehaviorSubject<{
    moduleConfig: MqttGetModuleConfigDto,
    isLoading: boolean,
    hasError: boolean
  }> = new BehaviorSubject<{
    moduleConfig: MqttGetModuleConfigDto,
    isLoading: boolean,
    hasError: boolean
  }>(null);
  public readonly moduleConfig$: Observable<{
    moduleConfig: MqttGetModuleConfigDto,
    isLoading: boolean,
    hasError: boolean
  }> = this.moduleConfigSubject.asObservable();

  public isMqttBlocked = false;
  private mqttBlockingSub: Subscription;

  constructor(
    private mqttBlockingService: MqttBlockingService,
    private dataService: DataService,
    private alertService: AlertService
  ) {
    this.mqttBlockingSub = this.mqttBlockingService.getBlockedGateways().subscribe((gateways) => {
      if (this.gatewayId) {
        this.isMqttBlocked = gateways.includes(this.gatewayId);
      }
    });
  }

  ngOnDestroy(): void {
    this.mqttBlockingSub.unsubscribe();
  }

  public async setGatewayId(gatewayId: string): Promise<void> {
    this.gatewayId = gatewayId;
    this.deviceDataSubject.next(undefined);
    await this.reloadDeviceData();
    Utils.initPumpSensorIds(this.deviceDataSubject.value?.deviceData);
  }

  public getDeviceData(): { deviceData: IDeviceData, isLoading: boolean, hasError: boolean } {
    return this.deviceDataSubject.value;
  }

  public getModuleConfig() {
    return this.moduleConfigSubject.value;
  }

  public reloadDeviceData$(gatewayId: string, updateIrValues: boolean = false, numberOfMeters: number = 1): Observable<void> {
    if (!gatewayId || this.isMqttBlocked) {
      return of(void 0);
    }

    this.deviceDataSubject.next({deviceData: undefined, isLoading: true, hasError: false});

    const irUpdate$ = updateIrValues
      ? this.dataService.updateIrValues$(gatewayId).pipe(
        switchMap(irUpdateValues => {
          if (!irUpdateValues || irUpdateValues.msg.toLowerCase() !== 'ok') {
            this.alertService.error("Fehler bei IR-Update für " + gatewayId + ": " + (irUpdateValues?.msg ?? 'unknown error') + ".");
            return EMPTY;
          }
          const totalSeconds = 15 * numberOfMeters;
          return timer(0, 1000).pipe(
            take(totalSeconds),
            tap(i => this.timerForIRUpdateSubject.next({timer: i, totalSeconds})),
            last(),
            finalize(() => this.timerForIRUpdateSubject.next(null)),
            map(() => void 0)
          );
        })
      )
      : of(void 0);

    return irUpdate$.pipe(
      switchMap(() => this.dataService.loadDeviceData$(gatewayId)),
      switchMap(rawData => {
        if (rawData == null) {
          this.alertService.error("Keine Daten für Gateway " + gatewayId + " gefunden");
          this.deviceDataSubject.next({deviceData: undefined, isLoading: false, hasError: true});
          return of(void 0);
        }
        this.deviceDataSubject.next({deviceData: rawData, isLoading: false, hasError: false});
        if (rawData.uvsModule.some(uvs => uvs.moduleInfo.uvsType === GatewayType.SM02 || uvs.moduleInfo.uvsType === GatewayType.SM03 || uvs.moduleInfo.uvsType === GatewayType.SM04 || uvs.moduleInfo.uvsType === GatewayType.SM05)) {
          return of(void 0).pipe(
            delay(0),
            switchMap(() => this.dataService.getImplementedMeters$(gatewayId)),
            tap(data => this.implementedMetersSubject.next(data?.implementedMeters ?? [])),
            map(() => void 0),
            catchError(() => of(void 0))
          );
        }
        return of(void 0);
      }),
      catchError(error => {
        this.deviceDataSubject.next({deviceData: undefined, isLoading: false, hasError: true});
        console.log(error);
        return of(void 0);
      })
    );
  }

  public async reloadDeviceData(updateIrValues: boolean = false, numberOfMeters: number = 1): Promise<void> {
    if (!this.gatewayId) return;
    return this.reloadDeviceData$(this.gatewayId, updateIrValues, numberOfMeters).toPromise();
  }

  public loadModuleConfig$(gatewayId?: string): Observable<void> {
    const id = gatewayId || this.gatewayId;
    if (!id || this.isMqttBlocked) {
      return of(void 0);
    }

    this.moduleConfigSubject.next({moduleConfig: null, isLoading: true, hasError: false});

    return this.dataService.loadModuleConfig$(id).pipe(
      tap(moduleConfig => {
        this.moduleConfigSubject.next({moduleConfig, isLoading: false, hasError: false});
      }),
      map(() => void 0),
      catchError(error => {
        this.moduleConfigSubject.next({moduleConfig: null, isLoading: false, hasError: true});
        console.log(error);
        return of(void 0);
      })
    );
  }

  public async loadModuleConfig(gatewayId?: string): Promise<void> {
    return this.loadModuleConfig$(gatewayId).toPromise();
  }

  public setDevice(getDevice: MqttGetDeviceDto): void {
    this.deviceSubject.next(getDevice);
  }

  public getDevice(): MqttGetDeviceDto {
    return this.deviceSubject.value;
  }

  public setInstallationData(installationData: IInstallationDataResponse): void {
    if (installationData && installationData.requestStatus && installationData.requestStatus !== 'ONGOING') {
      this.alertService.error('Das Gateway ist bereits installiert.');
    }
    this.installationDataSubject.next(installationData);
  }

  public getInstallationData(): IInstallationDataResponse {
    return this.installationDataSubject.value;
  }

  private sleep(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
